/**
 * OMNICHANNEL-TIMELINE-AND-LEAD-360-V1 -- Timeline: THE registered shared
 * activity-timeline primitive (the record-page spine).
 *
 * ONE chronological, filterable spine of every touch on a record: dense
 * day-grouped rows (channel icon | summary | actor | relative + absolute
 * time), channel + type filters over the loaded window, expandable lawful
 * detail (native <details>), honest windowing ("Showing X of Y" + Load
 * more -- the lead-inbox disclosure idiom), and the standing honest empty /
 * loading / error states via the registered EmptyState / LoadingState /
 * ErrorState primitives.
 *
 * THIS is the timeline-unification the V7-P4 / gap.r-40 carrier deferred to
 * (registry `component.condensed-timeline` dontRule: "a fourth timeline
 * renderer"): the LeadActivityRail feed renderer's day-group + icon-disc +
 * evidence-tag grammar is RE-HOSTED here as the shared primitive -- the
 * renderer count does not grow. First adopter: the Lead 360 record page
 * (Lead360CanonicalView center spine). Named next adopters: the COC
 * recovery console and Patient 360 (adapter-only -- build a
 * `TimelineEntry[]` from that surface's own lawful read; this component
 * needs NO modification to be adopted).
 *
 * TRUTH LAWS (A5): entries render IN RECEIVED ORDER -- this component never
 * re-sorts, never invents, and never collapses entries behind "+n"
 * concealment (a voided entry renders struck with its void metadata, never
 * hidden). Ordering determinism is the WIRE's contract (server merge:
 * occurredAt + provenance-rank tie-break; test-pinned). Filters are a
 * VIEW over the loaded window and say so when a window cap is active.
 *
 * PURE: no fetch, no clock (relative labels arrive pre-computed), no
 * data derivation beyond presence grouping. Tokens only (--ac-*); zero
 * decorative motion (the disclosure caret follows the existing
 * reduced-motion CSS).
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  Activity,
  CalendarDays,
  Eye,
  FileText,
  Footprints,
  Globe,
  MessageCircle,
  MessageSquare,
  Phone,
  User,
} from 'lucide-react';

import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import LoadingState from './LoadingState';

/**
 * THE EVENT ENVELOPE (A2), frontend contract: every entry a timeline
 * renders satisfies this shape. Adapters (per surface) map their lawful
 * read onto it -- occurred-at + actor + channel + type + one-line summary +
 * lawful detail payload + source pointer. Actor doctrine: `actorLine`
 * carries a human role, a configured agent operator, or "System" -- all
 * through the same field, no special shape per actor kind.
 */
export type TimelineChannel =
  | 'call'
  | 'whatsapp'
  | 'chat'
  | 'web'
  | 'walk_in'
  | 'system';

export type TimelineTypeFamily =
  | 'call'
  | 'appointment'
  | 'reveal'
  | 'person'
  | 'note'
  | 'system';

export interface TimelineEntry {
  /** Stable render key (adapter-derived; unique within the loaded window). */
  readonly key: string;
  /** ISO instant (the source record's own timestamp; audit truth). */
  readonly occurredAtIso: string;
  /** Zone-labeled day header text (e.g. "22 Jul 2026") -- adapter-computed
   *  from THE one IST formatter; consecutive equal labels group. */
  readonly dayLabel: string;
  /** Zone-labeled time text (e.g. "3:00 PM IST"). */
  readonly timeLabel: string;
  /** The full absolute stamp (tooltip/title + accessibility). */
  readonly whenLabel: string;
  /** Pre-computed relative age ("2 hr ago") or null (never fabricated). */
  readonly relativeLabel?: string | null;
  /** Channel dimension (A2) -- derived from REAL stored facts only. */
  readonly channel: TimelineChannel;
  readonly channelLabel: string;
  /** Type family (the existing rail icon families + 'note'). */
  readonly typeFamily: TimelineTypeFamily;
  /** The raw event-type token (data attribute; filter + test hook). */
  readonly eventType: string;
  /** One-line humanized consequence label. */
  readonly label: string;
  /** PHI-safe detail prose, actor-free (LEAD360-G6-AUTHOR-COLUMN-FIX --
   *  the adapter splits any trailing "by {actor}" clause OUT of this
   *  before it reaches here; see `authorLabel` for the actor itself), or
   *  null. */
  readonly actorLine?: string | null;
  /** The author column's own value -- the ACTOR ("Sandbox Agent",
   *  "COC", "System"), never the provenance CLASS. */
  readonly authorLabel?: string | null;
  /** Quiet provenance-CLASS tag (agent-recorded / integration-verified /
   *  ...) -- a DATA field only; NOT rendered in the row (G6-1) unless a
   *  future adopter's own approved mockup draws a non-primary treatment
   *  for it. */
  readonly evidenceLabel?: string | null;
  /** Voided entries render struck with this state -- never hidden. */
  readonly voided?: boolean;
  /** Lawful expandable detail payload (e.g. the note text); null = none. */
  readonly detail?: ReactNode | null;
  /** The disclosure summary for the detail ("View note"). */
  readonly detailSummaryLabel?: string | null;
}

export type TimelineStatus = 'loading' | 'error' | 'ready';

export interface TimelineProps {
  /** Entries NEWEST-FIRST as the wire delivered them (never re-sorted). */
  readonly entries: readonly TimelineEntry[];
  readonly status: TimelineStatus;
  /** Honest wired-vs-not empty copy (the standing timeline law). */
  readonly emptyTitle: string;
  readonly emptyBody?: string;
  /** Override the empty-state testid so adopters keep their pinned hooks
   *  (e.g. the standing lead360-activity-empty-wired / -not-wired ids). */
  readonly emptyTestId?: string;
  /** Error state copy + retry (rendered when status === 'error'). */
  readonly errorLabel?: string;
  readonly onRetry?: () => void;
  /** Windowing disclosure: total stored touches vs loaded window. */
  readonly totalCount?: number;
  readonly hasMore?: boolean;
  readonly onLoadMore?: () => void;
  readonly loadingMore?: boolean;
  /** Test-id prefix so adopters keep distinct hooks. Default 'ac-timeline'. */
  readonly testId?: string;
  /** Accessible region label ("Lead activity timeline"). */
  readonly ariaLabel: string;
}

/** Channel icon map (the existing lucide set -- presentation keying only). */
const CHANNEL_ICON: Readonly<Record<TimelineChannel, JSX.Element>> = {
  call: <Phone aria-hidden="true" />,
  whatsapp: <MessageCircle aria-hidden="true" />,
  chat: <MessageSquare aria-hidden="true" />,
  web: <Globe aria-hidden="true" />,
  walk_in: <Footprints aria-hidden="true" />,
  system: <Activity aria-hidden="true" />,
};

/** Type-family icon map (the rail's existing families + note). */
const TYPE_ICON: Readonly<Record<TimelineTypeFamily, JSX.Element>> = {
  call: <Phone aria-hidden="true" />,
  appointment: <CalendarDays aria-hidden="true" />,
  reveal: <Eye aria-hidden="true" />,
  person: <User aria-hidden="true" />,
  note: <FileText aria-hidden="true" />,
  system: <Activity aria-hidden="true" />,
};

export const TIMELINE_TYPE_FAMILY_LABEL: Readonly<
  Record<TimelineTypeFamily, string>
> = {
  call: 'Calls',
  appointment: 'Appointments',
  reveal: 'Reveals',
  person: 'Lifecycle',
  note: 'Notes',
  system: 'System',
};

/**
 * LEAD360-B2-G5-POLISH-AND-CLOSURE M3 (filter grammar single row):
 * chronicle-normal.png's primary row promotes exactly these three --
 * WhatsApp (the dominant patient-facing channel) + Calls + Notes (the two
 * activity families staff triage most) -- a fixed, curated short-list, not
 * a data-driven top-N. Every other channel/type actually present in the
 * loaded window folds behind "More filters" (never dropped -- A5).
 */
const PROMOTED_FILTER_CHANNEL: TimelineChannel = 'whatsapp';
const PROMOTED_FILTER_TYPES: readonly TimelineTypeFamily[] = ['call', 'note'];

/** Icon rule: the CHANNEL keys the disc where it is a real interaction
 *  channel; 'system' entries fall back to the type-family icon so a
 *  system-derived appointment still reads as an appointment. */
export function timelineEntryIcon(entry: TimelineEntry): JSX.Element {
  if (entry.channel !== 'system') return CHANNEL_ICON[entry.channel];
  return TYPE_ICON[entry.typeFamily];
}

/** Group consecutive entries by dayLabel (order preserved -- the rail's
 *  proven grouping, re-hosted; a pure presentation grouping, no re-sort). */
export function groupTimelineEntriesByDay(
  entries: readonly TimelineEntry[],
): ReadonlyArray<{
  readonly dayLabel: string;
  readonly entries: readonly TimelineEntry[];
}> {
  const groups: Array<{ dayLabel: string; entries: TimelineEntry[] }> = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.dayLabel === entry.dayLabel) {
      last.entries.push(entry);
    } else {
      groups.push({ dayLabel: entry.dayLabel, entries: [entry] });
    }
  }
  return groups;
}

/** Pure filter application (exported for deterministic tests). */
export function applyTimelineFilters(
  entries: readonly TimelineEntry[],
  channelFilter: TimelineChannel | null,
  typeFilter: TimelineTypeFamily | null,
): readonly TimelineEntry[] {
  return entries.filter(
    (e) =>
      (channelFilter === null || e.channel === channelFilter) &&
      (typeFilter === null || e.typeFamily === typeFilter),
  );
}

function FilterChip({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={
        active ? 'ac-timeline__filter ac-timeline__filter--active' : 'ac-timeline__filter'
      }
      aria-pressed={active}
      onClick={onClick}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

/**
 * LEAD360-B2-G5-POLISH-AND-CLOSURE M2 (entry row anatomy + density):
 * chronicle-normal/dense.png's row is ONE line -- icon / type / detail
 * inline / author column / relative-absolute IST time -- never a second
 * prose line below. The inline detail slot prefers the note's OWN body
 * (typeFamily 'note', a plain-string `detail`) over the generic
 * actor/summary line. LEAD360-G6-AUTHOR-COLUMN-FIX G6-1: `actorLine` is
 * now GUARANTEED actor-free by the adapter (`splitActorSuffix`,
 * lead-record-experience.ts) before it ever reaches this primitive --
 * this function stays a pure "which field wins" choice, no text-parsing
 * of its own. The "View note" / "Reschedule detail" expanders stay
 * exactly as named in M2 ("keep... only where extra detail exists") --
 * unconditional on `entry.detail` presence, unchanged from before this
 * pass.
 */
function entryInlineDetailText(entry: TimelineEntry): string | null {
  if (entry.typeFamily === 'note' && typeof entry.detail === 'string') {
    return entry.detail;
  }
  return typeof entry.actorLine === 'string' && entry.actorLine !== ''
    ? entry.actorLine
    : null;
}

function EntryRow({
  entry,
  testId,
}: {
  entry: TimelineEntry;
  testId: string;
}): JSX.Element {
  const inlineDetail = entryInlineDetailText(entry);
  return (
    <li
      className={
        entry.voided === true
          ? 'ac-timeline__entry ac-timeline__entry--voided'
          : 'ac-timeline__entry'
      }
      data-testid={`${testId}-entry`}
      data-event-type={entry.eventType}
      data-channel={entry.channel}
      data-type-family={entry.typeFamily}
      data-voided={entry.voided === true ? 'yes' : 'no'}
    >
      <span className="ac-timeline__marker" aria-hidden="true">
        {timelineEntryIcon(entry)}
      </span>
      <div className="ac-timeline__body">
        <div className="ac-timeline__head">
          <span className="ac-timeline__label">{entry.label}</span>
          {entry.voided === true ? (
            <span className="ac-tag ac-timeline__voided-tag" data-testid={`${testId}-voided-tag`}>
              Voided
            </span>
          ) : null}
          {inlineDetail !== null ? (
            <span
              className="ac-timeline__detail-inline"
              title={inlineDetail}
              data-testid={`${testId}-detail-inline`}
            >
              {inlineDetail}
            </span>
          ) : null}
          {/* LEAD360-G6-AUTHOR-COLUMN-FIX G6-1: the author column renders
              the ACTOR (authorLabel), never the provenance CLASS
              (evidenceLabel) -- the chronicle target mockup draws no
              secondary treatment for the class, so it does not render in
              the row at all (a pure data field now -- see current-truth-
              testing pins that still read it directly). */}
          {typeof entry.authorLabel === 'string' && entry.authorLabel !== '' ? (
            <span className="ac-timeline__author" data-testid={`${testId}-author`}>
              {entry.authorLabel}
            </span>
          ) : null}
          <span className="ac-timeline__when" title={entry.whenLabel}>
            {typeof entry.relativeLabel === 'string' && entry.relativeLabel !== ''
              ? `${entry.relativeLabel} - ${entry.timeLabel}`
              : entry.timeLabel !== ''
                ? entry.timeLabel
                : entry.whenLabel}
          </span>
        </div>
        {entry.detail !== null && entry.detail !== undefined ? (
          <details className="ac-timeline__detail" data-testid={`${testId}-detail`}>
            <summary className="ac-timeline__detail-summary">
              {entry.detailSummaryLabel ?? 'View detail'}
            </summary>
            <div className="ac-timeline__detail-body">{entry.detail}</div>
          </details>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The shared timeline. Renders filters ONLY for dimensions with more than
 * one distinct value in the loaded window (no dead filters); "All" resets.
 */
export default function Timeline({
  entries,
  status,
  emptyTitle,
  emptyBody,
  emptyTestId,
  errorLabel = 'The timeline could not be loaded.',
  onRetry,
  totalCount,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  testId = 'ac-timeline',
  ariaLabel,
}: TimelineProps): JSX.Element {
  const [channelFilter, setChannelFilter] = useState<TimelineChannel | null>(null);
  const [typeFilter, setTypeFilter] = useState<TimelineTypeFamily | null>(null);

  const channelsPresent = useMemo(() => {
    const seen = new Set<TimelineChannel>();
    for (const e of entries) seen.add(e.channel);
    return [...seen];
  }, [entries]);
  const typesPresent = useMemo(() => {
    const seen = new Set<TimelineTypeFamily>();
    for (const e of entries) seen.add(e.typeFamily);
    return [...seen];
  }, [entries]);
  // "No dead filters" (the standing law, preserved from the old two-row
  // grammar): a dimension with <= 1 distinct value in the loaded window
  // offers NOTHING to narrow, so it draws no chip at all here -- not even
  // a promoted one. Only a genuinely filterable dimension (>1 distinct
  // value) contributes to the row or the overflow.
  const channelFilterable = channelsPresent.length > 1;
  const typeFilterable = typesPresent.length > 1;
  const promotedChannelPresent =
    channelFilterable && channelsPresent.includes(PROMOTED_FILTER_CHANNEL);
  const promotedTypesPresent = typeFilterable
    ? PROMOTED_FILTER_TYPES.filter((t) => typesPresent.includes(t))
    : [];
  const remainingChannels = channelFilterable
    ? channelsPresent.filter((c) => c !== PROMOTED_FILTER_CHANNEL)
    : [];
  const remainingTypes = typeFilterable
    ? typesPresent.filter((t) => !PROMOTED_FILTER_TYPES.includes(t))
    : [];
  const hasMoreFilters = remainingChannels.length > 0 || remainingTypes.length > 0;
  const moreFiltersActive =
    (channelFilter !== null && remainingChannels.includes(channelFilter)) ||
    (typeFilter !== null && remainingTypes.includes(typeFilter));

  if (status === 'loading') {
    return (
      <section className="ac-timeline" aria-label={ariaLabel} data-testid={testId}>
        <LoadingState label="Loading the activity timeline..." lines={4} testId={`${testId}-loading`} />
      </section>
    );
  }
  if (status === 'error') {
    return (
      <section className="ac-timeline" aria-label={ariaLabel} data-testid={testId}>
        <ErrorState
          title={errorLabel}
          {...(onRetry !== undefined
            ? {
                action: (
                  <button
                    type="button"
                    className="ac-btn ac-btn--secondary ac-btn--sm"
                    onClick={onRetry}
                    data-testid={`${testId}-retry`}
                  >
                    Retry
                  </button>
                ),
              }
            : {})}
          testId={`${testId}-error`}
        />
      </section>
    );
  }

  const filtered = applyTimelineFilters(entries, channelFilter, typeFilter);
  const filterActive = channelFilter !== null || typeFilter !== null;
  const loadedCount = entries.length;
  const total = totalCount ?? loadedCount;

  return (
    <section className="ac-timeline" aria-label={ariaLabel} data-testid={testId}>
      {loadedCount > 0 && (channelsPresent.length > 1 || typesPresent.length > 1) ? (
        <div className="ac-timeline__filters" data-testid={`${testId}-filters`}>
          {/* LEAD360-B2-G5-POLISH-AND-CLOSURE M3: ONE row, both dimensions
              mixed (All / WhatsApp / Calls / Notes / More filters) --
              selecting a channel clears any active type and vice versa
              (single-select, matching the flat row's one-pill-active
              reading); "All" clears both. The long tail folds behind the
              native <details> "More filters" (expand-in-place, never a
              second visible row). */}
          <div
            className="ac-timeline__filter-row"
            role="group"
            aria-label="Filter activity"
            data-testid={`${testId}-filter-row`}
          >
            <FilterChip
              label="All"
              active={channelFilter === null && typeFilter === null}
              onClick={(): void => {
                setChannelFilter(null);
                setTypeFilter(null);
              }}
              testId={`${testId}-filter-all`}
            />
            {promotedChannelPresent ? (
              <FilterChip
                label={
                  entries.find((e) => e.channel === PROMOTED_FILTER_CHANNEL)
                    ?.channelLabel ?? PROMOTED_FILTER_CHANNEL
                }
                active={channelFilter === PROMOTED_FILTER_CHANNEL}
                onClick={(): void => {
                  setChannelFilter(
                    channelFilter === PROMOTED_FILTER_CHANNEL ? null : PROMOTED_FILTER_CHANNEL,
                  );
                  setTypeFilter(null);
                }}
                testId={`${testId}-filter-channel-${PROMOTED_FILTER_CHANNEL}`}
              />
            ) : null}
            {promotedTypesPresent.map((t) => (
              <FilterChip
                key={t}
                label={TIMELINE_TYPE_FAMILY_LABEL[t]}
                active={typeFilter === t}
                onClick={(): void => {
                  setTypeFilter(typeFilter === t ? null : t);
                  setChannelFilter(null);
                }}
                testId={`${testId}-filter-type-${t}`}
              />
            ))}
            {hasMoreFilters ? (
              <details
                className={
                  moreFiltersActive
                    ? 'ac-timeline__more-filters ac-timeline__more-filters--active'
                    : 'ac-timeline__more-filters'
                }
                data-testid={`${testId}-more-filters`}
              >
                <summary className="ac-timeline__more-filters-summary">
                  More filters
                </summary>
                <div className="ac-timeline__more-filters-body">
                  {remainingChannels.length > 0 ? (
                    <div
                      className="ac-timeline__filter-row"
                      role="group"
                      aria-label="Filter by channel"
                      data-testid={`${testId}-channel-filters`}
                    >
                      {remainingChannels.map((c) => (
                        <FilterChip
                          key={c}
                          label={entries.find((e) => e.channel === c)?.channelLabel ?? c}
                          active={channelFilter === c}
                          onClick={(): void => {
                            setChannelFilter(channelFilter === c ? null : c);
                            setTypeFilter(null);
                          }}
                          testId={`${testId}-filter-channel-${c}`}
                        />
                      ))}
                    </div>
                  ) : null}
                  {remainingTypes.length > 0 ? (
                    <div
                      className="ac-timeline__filter-row"
                      role="group"
                      aria-label="Filter by activity type"
                      data-testid={`${testId}-type-filters`}
                    >
                      {remainingTypes.map((t) => (
                        <FilterChip
                          key={t}
                          label={TIMELINE_TYPE_FAMILY_LABEL[t]}
                          active={typeFilter === t}
                          onClick={(): void => {
                            setTypeFilter(typeFilter === t ? null : t);
                            setChannelFilter(null);
                          }}
                          testId={`${testId}-filter-type-${t}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </details>
            ) : null}
          </div>
        </div>
      ) : null}

      {loadedCount === 0 ? (
        <EmptyState
          testId={emptyTestId ?? `${testId}-empty`}
          title={emptyTitle}
          {...(emptyBody !== undefined ? { body: emptyBody } : {})}
        />
      ) : filtered.length === 0 ? (
        <p className="ac-timeline__nomatch" data-testid={`${testId}-filter-empty`}>
          No loaded entries match this filter.
          {hasMore ? ' Older history is not loaded yet - load more to widen the search.' : ''}
        </p>
      ) : (
        <ol className="ac-timeline__days" data-testid={`${testId}-days`}>
          {groupTimelineEntriesByDay(filtered).map((group) => (
            <li
              className="ac-timeline__daygroup"
              key={`${group.dayLabel}:${group.entries[0]!.key}`}
            >
              <span className="ac-timeline__day" data-testid={`${testId}-day`}>
                {group.dayLabel}
              </span>
              <ol className="ac-timeline__dayevents">
                {group.entries.map((entry) => (
                  <EntryRow key={entry.key} entry={entry} testId={testId} />
                ))}
              </ol>
            </li>
          ))}
        </ol>
      )}

      {loadedCount > 0 ? (
        <div className="ac-timeline__foot" data-testid={`${testId}-foot`}>
          <span className="ac-timeline__count" data-testid={`${testId}-count`}>
            {filterActive
              ? `${filtered.length} of ${loadedCount} loaded ${loadedCount === 1 ? 'touch' : 'touches'} match`
              : `Showing ${loadedCount} of ${total} ${total === 1 ? 'touch' : 'touches'}`}
          </span>
          {hasMore && onLoadMore !== undefined ? (
            <button
              type="button"
              className="ac-btn ac-btn--secondary ac-btn--sm"
              onClick={onLoadMore}
              disabled={loadingMore}
              data-testid={`${testId}-load-more`}
            >
              {loadingMore ? 'Loading...' : 'Load older history'}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
